import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import { Megaphone, Plus, Rocket, X, Layers, Users, BookmarkPlus, Bookmark, Trash2, ArrowRight, ArrowLeft, FileBarChart, RefreshCw, Loader2, Paperclip, Image as ImageIcon, Video, FileText } from "lucide-react";
import api, { formatError } from "../lib/api";
import SegmentBuilder, { blankGroup } from "../components/SegmentBuilder";

const DEFAULT_TEMPLATE = "Hi {{name}}, you're invited to the next CBC milestone push. Reply YES to learn more.";

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [savedSegments, setSavedSegments] = useState([]);
  const [open, setOpen] = useState(false);
  const [reportFor, setReportFor] = useState(null);
  const nav = useNavigate();

  async function fetchAll() {
    const [c, s] = await Promise.all([api.get("/campaigns"), api.get("/segments")]);
    setItems(c.data);
    setSavedSegments(s.data);
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, s] = await Promise.all([api.get("/campaigns"), api.get("/segments")]);
        if (alive) { setItems(c.data); setSavedSegments(s.data); }
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  async function launch(id) {
    try { await api.post(`/campaigns/${id}/launch`); await fetchAll(); }
    catch (e) { alert(formatError(e)); }
  }

  async function deleteSegment(id) {
    if (!window.confirm("Delete this saved segment?")) return;
    try { await api.delete(`/segments/${id}`); await fetchAll(); }
    catch (e) { alert(formatError(e)); }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Engagement"
        title="Campaign Manager"
        subtitle="Build precise audiences with multi-criteria filters, preview live counts, then push WhatsApp or SMS campaigns."
        testId="campaigns-page"
        actions={<>
          <button onClick={() => nav("/cohorts")} className="btn-teal inline-flex items-center gap-2 !py-2 mr-2" data-testid="open-cohorts-btn"><Layers size={14} /> Cohort Dashboard</button>
          <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-campaign-btn"><Plus size={14} /> New campaign</button>
        </>}
      />

      {savedSegments.length > 0 && (
        <Card title="Saved segments" subtitle="Reuse these in future campaigns — counts refresh whenever you preview.">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
            {savedSegments.map((s) => (
              <div key={s.id} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-2" data-testid={`saved-segment-${s.id}`}>
                <div>
                  <div className="text-[13px] font-medium text-slate-800 flex items-center gap-1.5"><Bookmark size={12} className="text-teal-700" /> {s.name}</div>
                  {s.description && <div className="text-[11px] text-slate-500 mt-0.5">{s.description}</div>}
                  <div className="text-[10.5px] text-slate-400 mt-1">{s.last_count?.toLocaleString() || 0} matched · saved {new Date(s.created_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => deleteSegment(s.id)} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`del-segment-${s.id}`}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((c) => (
          <Card key={c.id} data-testid={`campaign-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Megaphone size={14} className="text-teal-700" />
                  <div className="font-medium text-slate-900">{c.name}</div>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{c.channel.toUpperCase()} · created {new Date(c.created_at).toLocaleDateString()}</div>
              </div>
              <Tag tone={c.status === "running" ? "teal" : c.status === "completed" ? "emerald" : "amber"}>{c.status}</Tag>
            </div>

            <div className="grid grid-cols-5 gap-2 mt-4">
              {[
                ["Sent", c.stats.sent],
                ["Delivered", c.stats.delivered],
                ["Opened", c.stats.opened],
                ["Clicked", c.stats.clicked],
                ["Converted", c.stats.converted],
              ].map(([k, v]) => (
                <div key={k} className="text-center">
                  <div className="serif-display text-xl numerals">{fmt(v)}</div>
                  <div className="small-caps text-slate-400 mt-0.5">{k}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-teal-700" style={{ width: `${c.stats.sent ? (c.stats.converted / c.stats.sent) * 100 : 0}%` }} />
            </div>
            <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
              <span>Conversion: {c.stats.sent ? ((c.stats.converted / c.stats.sent) * 100).toFixed(1) : 0}%</span>
              <div className="flex items-center gap-2">
                {c.status !== "scheduled" && (
                  <button onClick={() => setReportFor(c)} className="btn-ghost !py-1 !px-3 flex items-center gap-1.5 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid={`report-${c.id}`}>
                    <FileBarChart size={12} /> Delivery report
                  </button>
                )}
                {c.status === "scheduled" && <button onClick={() => launch(c.id)} className="btn-teal !py-1 !px-3 flex items-center gap-1.5" data-testid={`launch-${c.id}`}><Rocket size={12} /> Launch</button>}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {open && <NewCampaignWizard savedSegments={savedSegments} onClose={() => { setOpen(false); fetchAll(); }} />}
      {reportFor && <DeliveryReportModal campaign={reportFor} onClose={() => { setReportFor(null); fetchAll(); }} />}
    </div>
  );
}

// --------------------------------------------------------------------------
// New campaign wizard — 3 steps: Segment → Channel + template → Review & schedule
// --------------------------------------------------------------------------
function NewCampaignWizard({ onClose, savedSegments }) {
  const [step, setStep] = useState(1);
  const [filters, setFilters] = useState(blankGroup("and"));
  const [preview, setPreview] = useState(null);
  const [segmentChoice, setSegmentChoice] = useState("new"); // "new" or saved id
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [saveAs, setSaveAs] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [media, setMedia] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mediaErr, setMediaErr] = useState("");

  async function handleMediaFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 16 * 1024 * 1024) { setMediaErr("File too large (max 16 MB)."); return; }
    setMediaErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia({ media_id: data.id, url: data.url, media_type: data.media_type, mime_type: data.content_type, filename: data.filename });
    } catch (err) { setMediaErr(formatError(err)); }
    finally { setUploading(false); }
  }

  function loadSavedSegment(id) {
    const s = savedSegments.find((x) => x.id === id);
    if (s) setFilters(s.filters || blankGroup("and"));
  }

  async function submit() {
    if (!name.trim()) return alert("Please enter a campaign name");
    setSubmitting(true);
    try {
      let segment_id = null;
      if (saveAs.trim()) {
        const { data } = await api.post("/segments", { name: saveAs.trim(), filters });
        segment_id = data.id;
      }
      const payload = {
        name: name.trim(),
        channel,
        template,
        segment: { filters, ...(segment_id ? { segment_id } : {}) },
        ...(channel === "whatsapp" && media ? { media } : {}),
        ...(channel === "whatsapp" && templateId.trim() ? { template_id: templateId.trim() } : {}),
      };
      await api.post("/campaigns", payload);
      onClose();
    } catch (e) {
      alert(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-6xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="serif-display text-2xl">New campaign</h3>
            <div className="flex items-center gap-2 mt-2">
              {["Audience", "Channel & message", "Review"].map((label, i) => {
                const n = i + 1;
                return (
                  <div key={label} className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium ${step >= n ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-500"}`}>{n}</div>
                    <div className={`text-[12px] ${step >= n ? "text-slate-800" : "text-slate-400"}`}>{label}</div>
                    {n < 3 && <div className="w-8 h-px bg-slate-200" />}
                  </div>
                );
              })}
            </div>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Step 1: Segment builder */}
        {step === 1 && (
          <div className="space-y-3">
            {savedSegments.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-slate-500">Start from:</span>
                <select
                  value={segmentChoice}
                  onChange={(e) => { setSegmentChoice(e.target.value); if (e.target.value !== "new") loadSavedSegment(e.target.value); else setFilters(blankGroup("and")); }}
                  className="text-[13px] py-1.5"
                  data-testid="segment-choice"
                >
                  <option value="new">— New segment —</option>
                  {savedSegments.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.last_count?.toLocaleString() || 0})</option>)}
                </select>
              </div>
            )}
            <SegmentBuilder value={filters} onChange={setFilters} onPreview={setPreview} />
          </div>
        )}

        {/* Step 2: Channel + template */}
        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <label className="small-caps text-slate-500">Campaign name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q3 milestone push — Sports physios" className="w-full" data-testid="campaign-name" />
              </div>
              <div>
                <label className="small-caps text-slate-500">Channel</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" onClick={() => setChannel("whatsapp")} className={`text-[13px] border rounded-lg px-3 py-2 ${channel === "whatsapp" ? "border-teal-700 bg-teal-50 text-teal-900" : "border-slate-200"}`} data-testid="ch-whatsapp">WhatsApp</button>
                  <button type="button" onClick={() => setChannel("sms")} className={`text-[13px] border rounded-lg px-3 py-2 ${channel === "sms" ? "border-teal-700 bg-teal-50 text-teal-900" : "border-slate-200"}`} data-testid="ch-sms">SMS</button>
                </div>
              </div>
              <div>
                <label className="small-caps text-slate-500">Message template (supports {`{{name}}`})</label>
                <textarea value={template} onChange={(e) => setTemplate(e.target.value)} rows={6} className="w-full" data-testid="campaign-template" />
                <div className="text-[10.5px] text-slate-400 mt-1">{template.length} chars · ~{Math.ceil(template.length / 160)} SMS segments</div>
              </div>
              {channel === "whatsapp" && (
                <div className="space-y-2 border-t border-slate-100 pt-3">
                  <label className="small-caps text-slate-500 inline-flex items-center gap-1"><Paperclip size={11} /> Attach media (photo / video / document)</label>
                  {!media ? (
                    <label className="block border border-dashed border-slate-300 rounded-lg p-3 text-center cursor-pointer hover:bg-slate-50" data-testid="campaign-media-dropzone">
                      <input type="file" accept="image/*,video/mp4,video/3gpp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt" className="hidden" onChange={handleMediaFile} data-testid="campaign-media-input" />
                      <span className="text-[12px] text-slate-500">{uploading ? "Uploading…" : "Click to browse — image, MP4 video, or document (max 16 MB)"}</span>
                    </label>
                  ) : (
                    <div className="flex items-center justify-between border border-slate-200 rounded-lg p-2" data-testid="campaign-media-attached">
                      <div className="flex items-center gap-2 min-w-0">
                        {media.media_type === "image" ? <ImageIcon size={14} className="text-teal-700 shrink-0" /> : media.media_type === "video" ? <Video size={14} className="text-teal-700 shrink-0" /> : <FileText size={14} className="text-teal-700 shrink-0" />}
                        <span className="text-[12px] text-slate-700 truncate">{media.filename || media.media_type}</span>
                        <span className="tag tag-slate">{media.media_type}</span>
                      </div>
                      <button type="button" onClick={() => setMedia(null)} className="text-slate-400 hover:text-rose-600" data-testid="campaign-media-remove"><X size={14} /></button>
                    </div>
                  )}
                  {mediaErr && <div className="text-[11px] text-rose-600" data-testid="campaign-media-error">{mediaErr}</div>}
                  <div>
                    <label className="small-caps text-slate-500">Approved template ID <span className="text-slate-400 normal-case">(for business-initiated sends)</span></label>
                    <input value={templateId} onChange={(e) => setTemplateId(e.target.value)} placeholder="e.g. testing_fundle" className="w-full" data-testid="campaign-template-id" />
                  </div>
                  <div className="text-[10.5px] text-amber-700 bg-amber-50 border border-amber-100 rounded p-2">
                    WhatsApp requires a Meta-approved template to message physios outside the 24-hour reply window. Attach media + set the approved template ID for cold campaigns; free-form media works within 24h of a physio&apos;s last message.
                  </div>
                </div>
              )}
              <div>
                <label className="small-caps text-slate-500 inline-flex items-center gap-1"><BookmarkPlus size={11} /> Save segment as (optional)</label>
                <input value={saveAs} onChange={(e) => setSaveAs(e.target.value)} placeholder="e.g. Mumbai Sports Power Users" className="w-full" data-testid="save-segment-as" />
              </div>
            </div>

            <div className="surface p-4">
              <div className="small-caps text-slate-500 mb-2">Preview</div>
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="text-[11px] text-slate-500 mb-1">{channel.toUpperCase()} preview</div>
                <div className="bg-white rounded p-2 text-[13px] text-slate-800 whitespace-pre-wrap">{template.replace("{{name}}", preview?.sample?.[0]?.name?.split(" ")[0] || "Doctor")}</div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="serif-display text-xl numerals">{preview?.total?.toLocaleString() || 0}</div>
                  <div className="small-caps text-slate-400">Audience</div>
                </div>
                <div>
                  <div className="serif-display text-xl numerals">{(channel === "whatsapp" ? preview?.whatsapp_reachable : preview?.sms_reachable)?.toLocaleString() || 0}</div>
                  <div className="small-caps text-slate-400">Reachable</div>
                </div>
                <div>
                  <div className="serif-display text-xl numerals">{preview?.opted_out?.toLocaleString() || 0}</div>
                  <div className="small-caps text-slate-400">Opted out</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="surface p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <Stat label="Audience" value={preview?.total?.toLocaleString() || 0} />
                <Stat label="Channel" value={channel.toUpperCase()} />
                <Stat label="Reachable" value={(channel === "whatsapp" ? preview?.whatsapp_reachable : preview?.sms_reachable)?.toLocaleString() || 0} />
                <Stat label="Opted out" value={preview?.opted_out?.toLocaleString() || 0} />
              </div>
            </div>
            <Card title={name || "(no name)"} subtitle="Campaign will be scheduled — launch manually from the Campaigns list.">
              <div className="text-[12.5px] text-slate-700 whitespace-pre-wrap border border-slate-200 rounded p-3 bg-slate-50">
                {template}
              </div>
              {saveAs && <div className="text-[11px] text-teal-700 mt-2">Segment will be saved as &quot;<strong>{saveAs}</strong>&quot; for reuse.</div>}
            </Card>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between mt-5 pt-3 border-t border-slate-100">
          <button type="button" onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="text-[13px] text-slate-600 inline-flex items-center gap-1.5 hover:text-slate-900" data-testid="wiz-back">
            <ArrowLeft size={14} /> {step === 1 ? "Cancel" : "Back"}
          </button>
          <div className="flex items-center gap-2">
            {step === 1 && <div className="text-[12px] text-slate-500">{preview?.total?.toLocaleString() || 0} physios matched</div>}
            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && (!preview || preview.total === 0)}
                className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="wiz-next"
              >
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={submitting} className="btn-primary inline-flex items-center gap-1.5" data-testid="wiz-schedule">
                {submitting ? "Scheduling…" : "Schedule campaign"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="serif-display text-2xl numerals">{value}</div>
      <div className="small-caps text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Delivery report — per-recipient WhatsApp/SMS status + re-target undelivered
// --------------------------------------------------------------------------
const STATUS_TONE = {
  DELIVRD: "emerald", DELIVERED: "emerald", READ: "emerald", SENT: "teal",
  FAILED: "rose", UNDELIVERED: "rose", REJECTED: "rose",
};

function DeliveryReportModal({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retargeting, setRetargeting] = useState(false);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  async function load() {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}/delivery-report`);
      setData(data);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaign.id]);

  async function retarget(channel) {
    if (!window.confirm(`Re-send this campaign to all UNDELIVERED recipients via ${channel.toUpperCase()}?`)) return;
    setRetargeting(true); setErr(""); setFlash("");
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/retarget`, { channel });
      setFlash(data.retargeted ? `Re-targeted ${data.retargeted} recipient(s) via ${data.channel.toUpperCase()}.` : (data.note || "No undelivered recipients."));
      await load();
    } catch (e) { setErr(formatError(e)); }
    finally { setRetargeting(false); }
  }

  function exportCsv() {
    const rows = (data?.report || []).map((r) => [r.physio_name, r.mobile, r.erp_outlet_id, r.channel, r.status, r.delivered ? "yes" : "no", (r.reason || "").replace(/,/g, " "), r.sent_at || "", r.delivered_at || ""]);
    const head = ["Physio", "Mobile", "ERP ID", "Channel", "Status", "Delivered", "Reason", "Sent At", "Delivered At"];
    const csv = [head, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${campaign.name}-delivery.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }

  const s = data?.summary || { total: 0, sent: 0, delivered: 0, failed: 0, pending: 0 };
  const rows = (data?.report || []).filter((r) => {
    if (statusFilter === "delivered" && !r.delivered) return false;
    if (statusFilter === "undelivered" && r.delivered) return false;
    if (!q) return true;
    const hay = `${r.physio_name} ${r.mobile} ${r.erp_outlet_id} ${r.status}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-5xl p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="delivery-report-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="small-caps text-teal-700">{(data?.campaign?.channel || campaign.channel || "").toUpperCase()} delivery report</div>
            <h3 className="serif-display text-2xl mt-0.5">{campaign.name}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="close-report"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          {[["Total", s.total, "border-slate-300"], ["Sent", s.sent, "border-teal-500"], ["Delivered", s.delivered, "border-emerald-500"], ["Failed", s.failed, "border-rose-500"], ["Pending", s.pending, "border-amber-500"]].map(([k, v, cls]) => (
            <div key={k} className={`surface p-3 text-center border-l-2 ${cls}`} data-testid={`summary-${k.toLowerCase()}`}>
              <div className="serif-display text-2xl numerals">{fmt(v)}</div>
              <div className="small-caps text-slate-400 mt-0.5">{k}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search physio / mobile / ERP…" className="text-[13px] py-1.5 flex-1 min-w-[180px]" data-testid="report-search" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-[13px] py-1.5" data-testid="report-status-filter">
            <option value="all">All statuses</option>
            <option value="delivered">Delivered</option>
            <option value="undelivered">Undelivered</option>
          </select>
          <button onClick={load} className="btn-ghost border border-slate-200 rounded-lg !py-1.5 !px-3 flex items-center gap-1.5 text-[13px]" data-testid="report-refresh"><RefreshCw size={13} /> Refresh</button>
          <button onClick={exportCsv} className="btn-ghost border border-slate-200 rounded-lg !py-1.5 !px-3 text-[13px]" data-testid="report-export">Export CSV</button>
          <button onClick={() => retarget("whatsapp")} disabled={retargeting || !s.total} className="btn-teal !py-1.5 !px-3 flex items-center gap-1.5 text-[13px] disabled:opacity-50" data-testid="retarget-whatsapp">
            {retargeting ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Re-target · WhatsApp
          </button>
          <button onClick={() => retarget("sms")} disabled={retargeting || !s.total} className="btn-primary !py-1.5 !px-3 flex items-center gap-1.5 text-[13px] disabled:opacity-50" data-testid="retarget-sms">
            Re-target · SMS
          </button>
        </div>

        {flash && <div className="tag tag-emerald w-full justify-start mb-2" data-testid="report-flash">{flash}</div>}
        {err && <div className="tag tag-rose w-full justify-start mb-2" data-testid="report-error">{err}</div>}

        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="max-h-[45vh] overflow-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-slate-500">
                  <th className="px-3 py-2 font-medium">Physio</th>
                  <th className="px-3 py-2 font-medium">Mobile</th>
                  <th className="px-3 py-2 font-medium">Channel</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody data-testid="report-table-body">
                {loading ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400"><Loader2 size={18} className="animate-spin inline" /> Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400" data-testid="report-empty">No delivery records yet. Launch the campaign to generate a report.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.delivery_id} className="border-t border-slate-100" data-testid={`report-row-${r.delivery_id}`}>
                    <td className="px-3 py-2">
                      <div className="text-slate-800">{r.physio_name}</div>
                      <div className="text-[10.5px] text-slate-400">{r.erp_outlet_id || "—"}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-600">{r.mobile || "—"}</td>
                    <td className="px-3 py-2">{(r.channel || "").toUpperCase()}</td>
                    <td className="px-3 py-2"><Tag tone={STATUS_TONE[r.status] || "slate"}>{r.delivered ? "Delivered" : (r.status || "—")}</Tag></td>
                    <td className="px-3 py-2 text-slate-500">{r.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="text-[10.5px] text-slate-400 mt-2">Delivery status updates live as Karix delivery callbacks arrive on the webhook. "Re-target" re-sends to every recipient not yet marked delivered.</div>
      </div>
    </div>
  );
}
